import { Injectable } from '@angular/core';
import { OfflineDbService } from './offline-db.service';
import { DEVICE_KEY_ID } from './offline-db.types';

// 1 MiB — matches the chunk size the P0 playback spike already proved works for
// progressive-file decrypt-in-chunks playback (docs/refactor/offline-P0-findings.md).
export const MEDIA_CHUNK_SIZE_BYTES = 1 * 1024 * 1024;

export interface EncryptedBlob {
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
}

@Injectable({
  providedIn: 'root',
})
export class OfflineCryptoService {
  private keyPromise: Promise<CryptoKey> | null = null;

  constructor(private offlineDb: OfflineDbService) {}

  // Non-extractable AES-256-GCM key, generated once and persisted in IndexedDB. The raw
  // key bytes never exist in JS, including to this code — see docs/offline-mode-plan.md §3.
  getOrCreateDeviceKey(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = this.loadOrCreateDeviceKey().catch((err) => {
        this.keyPromise = null;
        throw err;
      });
    }
    return this.keyPromise;
  }

  // The key is memoised for the page's lifetime, so anything that destroys the stored key
  // record must say so here. Otherwise this service keeps handing out a CryptoKey that no
  // longer exists on disk: content saved after a wipe encrypts against it, the next launch
  // generates a fresh key, and that content is permanently unreadable.
  resetKeyCache(): void {
    this.keyPromise = null;
  }

  private async loadOrCreateDeviceKey(): Promise<CryptoKey> {
    const existing = await this.offlineDb.getDeviceKeyRecord();
    if (existing) return existing.key;

    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    // Another copy of the app (a note's video iframe, another tab) may be creating one at this
    // very moment. Only one may win, and everyone must use the winner - see
    // claimDeviceKeyRecord. Never put() here: overwriting a stored key makes everything already
    // encrypted with it unreadable.
    const winner = await this.offlineDb.claimDeviceKeyRecord({ id: DEVICE_KEY_ID, key, createdAt: Date.now() });
    return winner.key;
  }

  // Single-shot encrypt/decrypt — used by `content` (lesson/note JSON, nav shell), which
  // is always well under 1MiB, so chunking it would be pure overhead.
  async encryptBlob(key: CryptoKey, plaintext: ArrayBuffer): Promise<EncryptedBlob> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
    return { iv, ciphertext };
  }

  async decryptBlob(key: CryptoKey, iv: Uint8Array, ciphertext: ArrayBuffer): Promise<ArrayBuffer> {
    return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  }

  // Chunked encrypt/decrypt — defined now for `media` per the plan's §8 schema, with no
  // callers in P1. Each chunk gets its own random IV; a chunk is never reused across
  // encryptions of the same plaintext. P4/P5 wire this up for document/media download.
  async *encryptChunked(key: CryptoKey, data: ArrayBuffer, chunkSizeBytes = MEDIA_CHUNK_SIZE_BYTES): AsyncGenerator<EncryptedBlob> {
    for (let offset = 0; offset < data.byteLength; offset += chunkSizeBytes) {
      const chunk = data.slice(offset, Math.min(data.byteLength, offset + chunkSizeBytes));
      yield this.encryptBlob(key, chunk);
    }
  }

  async decryptChunked(key: CryptoKey, chunks: EncryptedBlob[]): Promise<ArrayBuffer[]> {
    return Promise.all(chunks.map((chunk) => this.decryptBlob(key, chunk.iv, chunk.ciphertext)));
  }
}
